import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// features
import { socketUserDocumentGeneratedReceivedSuccess } from '../../sockets/actions';
import { updateLoadDriverInvoiceSuccess } from '../../dispatch-details/load/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature payroll
import * as A from './actions';
import { setInitialState } from '../actions';
import { getPayrollStatisticListAndCurrency } from '../helpers';
//////////////////////////////////////////////////

const initialState = {
  totalCount: 0,
  visited: false,
  loading: false,
  itemList: null,
  filterParams: {},
  usedReport: null,
  titleSortValues: {},
  reportPending: false,
  updatedInvoice: null,
  tableTitleFilters: {},
  availableReports: null,
  recallDocumentsGuid: null,
  currentBranchConfigs: null,
  availableCustomExportTypes: [],
  pagination: { limit: 20, offset: 0 },
};

const setVendorInitialState = () => initialState;

const setVisited = (state, data=true) => (
  P.$set('visited', data, state)
);

const setReportPending = (state) => (
  P.$set('reportPending', true, state)
);

const setReports = (state, data) => (
  P.$set('availableReports', data, state)
);

const setUsedReport = (state, data) => (
  P.$all(
    P.$set('itemList', null),
    P.$set('usedReport', data),
    P.$set('titleSortValues', {}),
    P.$set('reportPending', false),
    P.$set('tableTitleFilters', {}),
    P.$set('pagination', initialState.pagination),
    state,
  )
);

const setQuickFilterParams = (state, data) => (
  P.$set('filterParams', data, state)
);

const setListLoading = (state, data) => (
  P.$set('loading', data, state)
);

const resetListAndPagination = (state) => (
  P.$all(
    P.$set('itemList', null),
    P.$set('pagination', initialState.pagination),
    state,
  )
);

const getItemListSuccess = (state, data) => {
  const { itemList, pagination } = state;
  const newOffset = R.add(pagination.offset, pagination.limit);
  const indexAdditional = G.ifElse(
    R.isNil(itemList),
    0,
    R.length(R.values(itemList)),
  );
  const newPayrolls = data.results.map((p, i) => R.mergeRight(
    p,
    {
      selected: false,
      expanded: false,
      index: R.add(i, indexAdditional),
    },
  ));
  const newList = R.mergeRight(itemList, R.indexBy(R.prop(GC.FIELD_GUID), newPayrolls));
  return P.$all(
    P.$set('itemList', newList),
    P.$set('totalCount', data.totalCount),
    P.$set(
      'pagination.offset',
      G.ifElse(
        R.gt(data.totalCount, newOffset),
        newOffset,
        data.totalCount,
      ),
    ),
    state,
  );
};

const selectItem = (state, data) => {
  if (R.equals(data, 'all')) {
    const { itemList } = state;
    const value = R.not(R.all(
      (item) => item.selected,
      R.values(itemList),
    ));
    return P.$set(
      'itemList',
      R.map(
        (item) => R.assoc('selected', value, item),
        itemList,
      ),
      state,
    );
  }
  return P.$toggle(`itemList.${data}.selected`, state);
};

const togglePayrollDetails = (state, data) => (
  P.$toggle(`itemList.${data}.expanded`, state)
);

const setPayrollDetailsLoading = (state, data) => (
  P.$set(`itemList.${data}.detailsLoading`, true, state)
);

const getPayrollDetailsSuccess = (state, data) => (
  P.$set(
    `itemList.${data.guid}`,
    R.mergeRight(
      R.path(['itemList', data.guid], state),
      {
        detailsError: false,
        details: data.details,
        detailsLoading: false,
      },
    ),
    state,
  )
);

const setPayrollStatisticLoading = (state, { guid, loading }) => (
  P.$set(`itemList.${guid}.statisticLoading`, loading, state)
);

const getPayrollStatisticSuccess = (state, { guid, statistic }) => {
  const { statisticList, statisticCurrency } = getPayrollStatisticListAndCurrency(statistic);

  return P.$all(
    P.$set(`itemList.${guid}.statistic`, statisticList),
    P.$set(`itemList.${guid}.statisticCurrency`, statisticCurrency),
    state,
  );
};

const getPayrollDetailsError = (state, data) => (
  P.$all(
    P.$set(`itemList.${data}.detailsError`, true),
    P.$set(`itemList.${data}.detailsLoading`, false),
    state,
  )
);

const collapseAndResetPayrollInvoices = (state, data) => (
  P.$set(
    `itemList.${data.guid}`,
    R.mergeRight(
      R.path(['itemList', data.guid], state),
      {
        ...data,
        details: null,
        statistic: null,
        expanded: false,
      },
    ),
    state,
  )
);

const removePayrollInvoiceFromUI = (state, data) => {
  const guid = R.path(['payroll', 'guid'], data);
  const item = R.path(['itemList', guid], state);
  return P.$set(
    `itemList.${guid}`,
    R.assoc(
      'details',
      R.filter(
        (invoice) => G.notEquals(invoice.guid, data.invoiceGuid),
        item.details,
      ),
      R.mergeRight(item, data.payroll),
    ),
    state,
  );
};

const removeVendorPayrollFromUI = (state, data) => (
  P.$all(
    P.$drop(`itemList.${data}`),
    P.$set('totalCount', R.dec(state.totalCount)),
    state,
  )
);

const updateVendorInvoiceSuccess = (state, data) => {
  const { invoice, updateFrom } = data;

  if (R.or(
    G.isNilOrEmpty(R.path(['itemList'], state)),
    R.pathEq(GC.INVOICE_TYPE_FLEET_INVOICE, [GC.FIELD_TYPE], invoice),
  )) return state;

  const mappedInvoice = G.mapDropdownsObjectInEntity(
    GC.FIELD_DISPLAYED_VALUE,
    [GC.FIELD_INVOICE_STATUS, GC.FIELD_MODE],
    invoice,
  );

  if (R.equals(updateFrom, 'addPayroll')) {
    return P.$set('updatedInvoice', mappedInvoice, state);
  }

  const payrollGuid = R.prop(GC.FIELD_FLEET_VENDOR_PAYROLL_GUID, invoice);
  const invoiceGuid = R.prop(GC.FIELD_GUID, invoice);
  const grandTotal = R.path(['payrollTotals', 'grandTotal'], invoice);
  const invoicesTotal = R.path(['payrollTotals', 'invoicesTotal'], invoice);
  const previousDetails = R.path(['itemList', payrollGuid, 'details'], state);

  let newPayroll = {
    ...R.path(['itemList', payrollGuid], state),
    grandTotal,
    invoicesTotal,
  };

  if (G.isNotNilAndNotEmpty(previousDetails)) {
    const details = R.map((item) => {
      if (R.equals(G.getGuidFromObject(item), invoiceGuid)) {
        return R.assoc(
          GC.FIELD_TEL_PRIMARY_REFERENCE_VALUE,
          R.prop(GC.FIELD_TEL_PRIMARY_REFERENCE_VALUE, item),
          mappedInvoice,
        );
      }

      return item;
    }, previousDetails);

    newPayroll = {
      ...newPayroll,
      details,
    };
  }

  return P.$set(`itemList.${payrollGuid}`, newPayroll, state);
};

const onSocketUserDocumentGeneratedReceivedSuccess = (state) => (
  P.$set('recallDocumentsGuid', G.generateGuid(), state)
);

const getVendorFuelTollCountsSuccess = (state, { count, countName, ...data }) => {
  if (R.isNotNil(countName)) {
    return P.$set(`fuelTollCounts.${countName}`, count, state);
  }

  return P.$set('fuelTollCounts', data, state);
};

const massUpdatePayrollStatusSuccess = (state, data) => {
  const indexed = R.indexBy(R.prop(GC.FIELD_GUID), R.or(data, []));

  const mappedList = R.map((item) => ({
    ...item,
    selected: false,
    [GC.GRC.STATUS_DISPLAYED_VALUE]: R.pathOr(
      R.prop(GC.GRC.STATUS_DISPLAYED_VALUE, item),
      [G.getGuidFromObject(item), GC.FIELD_STATUS, GC.FIELD_DISPLAYED_VALUE],
      indexed,
    ),
  }), R.propOr([], 'itemList', state));

  return P.$set('itemList', mappedList, state);
};

const getAvailableCustomExportTypesSuccess = (state, data) => (
  P.$set('availableCustomExportTypes', data, state)
);

export default createReducer({
  [A.selectItem]: selectItem,
  [A.setReports]: setReports,
  [A.setVisited]: setVisited,
  [A.setUsedReport]: setUsedReport,
  [A.setListLoading]: setListLoading,
  [A.setReportPending]: setReportPending,
  [setInitialState]: setVendorInitialState,
  [A.setTableTitleSort]: G.setTableTitleSort,
  [A.getItemListSuccess]: getItemListSuccess,
  [A.setTableTitleFilter]: G.setTableTitleFilter,
  [A.togglePayrollDetails]: togglePayrollDetails,
  [A.setQuickFilterParams]: setQuickFilterParams,
  [A.resetListAndPagination]: resetListAndPagination,
  [A.getPayrollDetailsError]: getPayrollDetailsError,
  [A.setPayrollDetailsLoading]: setPayrollDetailsLoading,
  [A.getPayrollDetailsSuccess]: getPayrollDetailsSuccess,
  [A.removeVendorPayrollFromUI]: removeVendorPayrollFromUI,
  [A.setPayrollStatisticLoading]: setPayrollStatisticLoading,
  [A.getPayrollStatisticSuccess]: getPayrollStatisticSuccess,
  [A.updateVendorInvoiceSuccess]: updateVendorInvoiceSuccess,
  [A.removePayrollInvoiceFromUI]: removePayrollInvoiceFromUI,
  [A.massUpdatePayrollStatusSuccess]: massUpdatePayrollStatusSuccess,
  [A.getVendorFuelTollCountsSuccess]: getVendorFuelTollCountsSuccess,
  [A.collapseAndResetPayrollInvoices]: collapseAndResetPayrollInvoices,
  [A.getAvailableCustomExportTypesSuccess]: getAvailableCustomExportTypesSuccess,
  //
  [updateLoadDriverInvoiceSuccess]: updateVendorInvoiceSuccess,
  [socketUserDocumentGeneratedReceivedSuccess]: onSocketUserDocumentGeneratedReceivedSuccess,
}, initialState);
